import { type Subscription, createConsumer } from "@rails/actioncable";
import React, { createContext, type PropsWithChildren, useContext, useRef, useCallback } from "react";
import {
  clearSocketConnectionId,
  getSocketConnectionId,
  updateSocketConnectionId,
} from "../analytics/socket_connections";
import { generateTrackingId } from "../analytics/utilities";
import type { ConversationTurnRouting } from "../types";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Types / Constants

const REQUEST_TIMEOUT_MS = 30000; // 30 seconds timeout

export enum WebSocketMessageType {
  RequestReceived = "request_received",
  RequestCompleted = "request_completed",
  RequestUpdate = "request_update",
  RequestFailed = "request_failed",
  RequestCancelled = "request_cancelled",
}

export enum WebSocketAction {
  Create = "create",
  Cancel = "cancel",
}

export enum WebSocketChannel {
  Conversations = "ConversationsChannel",
}

export type WebSocketMessage = {
  message: WebSocketMessageType;
  action: WebSocketAction;
  query: string;
  conversation?: unknown; // Raw WebSocket data, not validated/transformed
  conversation_turn?: unknown; // Raw WebSocket data, not validated/transformed
  routing?: ConversationTurnRouting;
  content?: string;
  error?: string;
};

type RequestDetails = {
  callback: (data: WebSocketMessage) => void;
  timeout: NodeJS.Timeout;
  requestId: string;
};

type QueuedRequest = {
  callback: (data: WebSocketMessage) => void;
  params: Record<string, unknown>;
  options?: WebSocketRequestOptions;
  requestId: string;
};

export type WebSocketRequestOptions = {
  queueable?: boolean;
};

export type WebSocketContextType = {
  submitRequest: (
    channel: WebSocketChannel,
    action: WebSocketAction,
    key: string,
    callback: (data: WebSocketMessage) => void,
    params: Record<string, unknown>,
    options?: WebSocketRequestOptions,
  ) => void;
  clearRequest: (channel: WebSocketChannel, action: WebSocketAction, key: string) => void;
  isConnected: (channel: WebSocketChannel) => boolean;
};

type WebSocketsProviderProps = PropsWithChildren<{
  apiToken: string;
  rootURL: string;
}>;

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Provider Logic

export const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export function WebSocketsProvider({ apiToken, rootURL, children }: WebSocketsProviderProps) {
  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Connection State Management

  const connectionStates = useRef<Record<WebSocketChannel, boolean>>({
    [WebSocketChannel.Conversations]: false,
  });

  const isConnected = useCallback((channel: WebSocketChannel) => {
    return connectionStates.current[channel] || false;
  }, []);

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Subscription Management

  // Track the active subscription to the ConversationsChannel
  const subscriptions = useRef<Record<WebSocketChannel, Subscription | undefined>>({
    [WebSocketChannel.Conversations]: undefined,
  });

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Request Management

  // Track all requests with their details
  const requests = useRef<Record<WebSocketChannel, Record<WebSocketAction, Record<string, RequestDetails>>>>({
    [WebSocketChannel.Conversations]: {
      [WebSocketAction.Create]: {},
      [WebSocketAction.Cancel]: {},
    },
  });

  // Queue for requests when disconnected
  const requestQueue = useRef<Record<WebSocketChannel, Record<WebSocketAction, Record<string, QueuedRequest>>>>({
    [WebSocketChannel.Conversations]: {
      [WebSocketAction.Create]: {},
      [WebSocketAction.Cancel]: {},
    },
  });

  // Helper method for getting a request, given a channel, action, and key
  const requestFor = useCallback((channel: WebSocketChannel, action: WebSocketAction, key: string) => {
    return requests.current[channel]?.[action]?.[key];
  }, []);

  // Clear out a request from our request tracking, deleting our timeout and removing the request from our tracking
  const clearRequest = useCallback(
    (channel: WebSocketChannel, action: WebSocketAction, key: string) => {
      const request = requestFor(channel, action, key);
      if (request) {
        clearTimeout(request.timeout);
        delete requests.current[channel][action][key];
      }
      // Also clear from queue if present
      delete requestQueue.current[channel][action][key];
    },
    [requestFor],
  );

  // Dispatch an error and clear out the request if it we don't receive a response by the timeout
  const handleRequestTimeout = useCallback(
    (channel: WebSocketChannel, action: WebSocketAction, key: string) => {
      const request = requestFor(channel, action, key);
      if (request) {
        request.callback({
          message: WebSocketMessageType.RequestFailed,
          action,
          query: key,
          error: `Request timed out after ${REQUEST_TIMEOUT_MS / 1000} seconds`,
        });

        clearRequest(channel, action, key);
      }
    },
    [clearRequest, requestFor],
  );

  // Submit a request to the server, handling timeouts and request tracking
  const submitRequest = useCallback(
    (
      channel: WebSocketChannel,
      action: WebSocketAction,
      key: string,
      callback: (data: WebSocketMessage) => void,
      params: Record<string, unknown>,
      options: WebSocketRequestOptions = { queueable: true },
      requestId?: string,
    ) => {
      const subscription = subscriptions.current[channel];

      // Can't submit a request if we never set up a subscription
      if (!subscription) return;

      // Don't submit a request if we already have a pending request for this query
      if (requestFor(channel, action, key)) return;

      // Ensure we have a valid requestId
      if (!requestId) {
        requestId = generateTrackingId();
      }

      // If not connected and request is queueable, add to queue
      const { queueable } = options;
      if (!isConnected(channel) && queueable) {
        // Replace any existing queued request with the same key
        requestQueue.current[channel][action][key] = {
          callback,
          params,
          options,
          requestId,
        };
        return;
      }

      // Set up timeout to throw an error if we don't receive a response by the timeout
      const timeout = setTimeout(() => handleRequestTimeout(channel, action, key), REQUEST_TIMEOUT_MS);

      // Register request details
      requests.current[channel][action][key] = {
        callback,
        timeout,
        requestId,
      };

      // Send the request with tracking IDs
      subscription.perform(action, { ...params, request_id: requestId, socket_connection_id: getSocketConnectionId() });
    },
    [handleRequestTimeout, requestFor, isConnected],
  );

  // Reset the timeout for a request, extending its lifetime
  const resetRequestTimeout = useCallback(
    (channel: WebSocketChannel, action: WebSocketAction, key: string) => {
      const request = requestFor(channel, action, key);
      if (request) {
        clearTimeout(request.timeout);
        request.timeout = setTimeout(() => handleRequestTimeout(channel, action, key), REQUEST_TIMEOUT_MS);
      }
    },
    [handleRequestTimeout, requestFor],
  );

  // Process queued requests when connection is restored for a specific channel
  const processQueuedRequests = useCallback(
    (connectedChannel: WebSocketChannel) => {
      if (!isConnected(connectedChannel)) return;

      const channelQueue = requestQueue.current[connectedChannel];
      Object.entries(channelQueue).forEach(([action, requests]) => {
        Object.entries(requests).forEach(([key, request]) => {
          // Re-submit the request
          submitRequest(
            connectedChannel,
            action as WebSocketAction,
            key,
            request.callback,
            request.params,
            request.options,
            request.requestId,
          );
        });
      });

      // Clear the queue for this channel after processing
      requestQueue.current[connectedChannel] = {
        [WebSocketAction.Create]: {},
        [WebSocketAction.Cancel]: {},
      };
    },
    [submitRequest, isConnected],
  );

  // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  // Initialization

  // Create the consumer for our subscriptions
  const consumer = createConsumer(`${rootURL}/cable?token=${encodeURIComponent(apiToken)}`);

  // Create a subscription for each channel
  const channels = Object.values(WebSocketChannel);
  for (const channel of channels) {
    // Don't create a subscription if we already have one
    if (subscriptions.current[channel]) continue;

    // Create the subscription with the appropriate callbacks
    const channelCallbacks = {
      connected() {
        updateSocketConnectionId();
        connectionStates.current[channel] = true;
        processQueuedRequests(channel);
      },
      disconnected() {
        clearSocketConnectionId();
        connectionStates.current[channel] = false;
      },
      received(data: WebSocketMessage) {
        const { action, query, message } = data;

        const request = requestFor(channel, action, query);
        request?.callback(data);

        if (
          [
            WebSocketMessageType.RequestCompleted,
            WebSocketMessageType.RequestFailed,
            WebSocketMessageType.RequestCancelled,
          ].includes(message)
        ) {
          // Clear out any completed, failed, or cancelled requests
          clearRequest(channel, action, query);
        } else {
          // Reset the timeout for any other messages (give more time for the response to completely finish)
          resetRequestTimeout(channel, action, query);
        }
      },
    };

    const subscription = consumer.subscriptions.create({ channel: channel }, channelCallbacks);
    subscriptions.current[channel] = subscription;
  }

  return (
    <WebSocketContext.Provider
      value={{
        submitRequest,
        clearRequest,
        isConnected,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
}

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Context Hook

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error("useWebSocket must be used within a WebSocketsProvider");
  }
  return context;
}
